import { useForm, getFormProps } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { Box, Typography, Button } from "@mui/material";
import { Role } from "api/auth";
import { findRelevesHorsParcByParticulier } from "api/releves";
import { ButtonReset } from "components/Buttons";
import { FromError } from "components/FromError";
import FullScreenLoading from "components/FullScreenLoading";
import { CodeSocieteInput } from "components/input/CodeSocieteInput";
import { DateConform } from "components/input/DateField";
import { LieuNaissanceInput } from "components/input/LieuNaissanceInput";
import { NomInput } from "components/input/NomInput";
import { PrenomInput } from "components/input/PrenomInput";
import { SelectCivilite } from "components/input/Selects";
import { setDemandesDeblocage } from "pages/RechercheRIHorsParc/api";
import { criteresHorsParcParticulier } from "pages/RechercheRIHorsParc/models/criteresHorsParc";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ActionFunctionArgs, useFetcher, useNavigate } from "react-router-dom";
import { ErrorType } from "utils/ErrorType";
import { useUser } from "utils/useGetUser";

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, { schema: criteresHorsParcParticulier });

  if (submission.status !== "success") {
    return submission.reply();
  }

  try {
    const result = await findRelevesHorsParcByParticulier(submission.value);

    if ("error" in result && result.error != null) {
      const error = result as ErrorType;
      return submission.reply({ formErrors: [error.error ?? ""] });
    }

    if ("erreur" in result.response && result.response.erreur !== null) {
      return submission.reply({ formErrors: [result.response.erreur ?? ""] });
    }
    // Todo: create session and navigate screen 3
    // if (result.response.demandesDeblocage.length === 0) {
    //
    // }

    // Enregistrer les données et naviguer vers écran 2 (selectionDemande)
    console.log("setDemandesDeblocage", result);
    setDemandesDeblocage(result.response);
    return { ok: true, redirectTo: "/recherche-ri-hors-parc/selectionDemande" };
  } catch (e: any) {
    console.log(e);
    return submission.reply({ formErrors: [e?.error ?? ""] });
  }
}

export function FormParticulier() {
  type ActionSuccess = { ok: true; redirectTo: string };

  const fetcher = useFetcher<typeof action>();
  const lastResult = fetcher.data as any;

  const navigate = useNavigate();

  useEffect(() => {
    const data = fetcher.data as ActionSuccess | undefined;
    if (data?.ok) {
      navigate(data.redirectTo);
    }
  }, [fetcher.data, navigate]);

  const [form, fields] = useForm({
    lastResult,
    shouldValidate: "onBlur",
    shouldRevalidate: "onBlur",
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: criteresHorsParcParticulier });
    },
  });

  const { t } = useTranslation("common");
  const user = useUser();

  return (
    <fetcher.Form method="post" action="/recherche-particulier-action" {...getFormProps(form)}>
      <FullScreenLoading loading={fetcher.state !== "idle"} />
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        <Typography fontSize={16} color="primary" fontWeight="bold" width="65%">
          {t("criteresHorsParc.criteresObligatoires")}
        </Typography>

        <SelectCivilite
          label={t("criteresHorsParc.form.civilite")}
          field={fields.civilite}
          required
          fullWidth
        />

        <NomInput label={t("criteresHorsParc.form.nom")} required field={fields.nom} fullWidth />
        <PrenomInput
          label={t("criteresHorsParc.form.prenom")}
          required
          field={fields.prenom}
          fullWidth
        />
        <DateConform
          label={t("criteresHorsParc.form.dateNaissance")}
          fullWidth
          required
          switchFormat
          openTo="year"
          field={fields.dateNaissance}
        />

        {user.role === Role.INTERMEDIAIRE && (
          <CodeSocieteInput
            type="assureur"
            label={t("criteresHorsParc.form.codeAssureur")}
            field={fields.codeAssureur}
            fullWidth
            required
          />
        )}

        {user.role === Role.ASSUREUR && (
          <input type="hidden" name="codeAssureur" value={user.codeSociete} />
        )}

        <Typography mt={2} fontSize={16} color="primary" fontWeight="bold" width="65%">
          {t("criteresHorsParc.criteresFacultatifs")}
        </Typography>

        <NomInput
          label={t("criteresHorsParc.form.nomNaissance")}
          field={fields.nomNaissance}
          fullWidth
        />
        <LieuNaissanceInput
          label={t("criteresHorsParc.form.lieuNaissance")}
          field={fields.lieuNaissance}
          fullWidth
        />

        <FromError errors={form.errors} />

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <ButtonReset />
          <Button
            type="submit"
            variant="contained"
            sx={{ bgcolor: "primary.dark", color: "white" }}
            disabled={fetcher.state !== "idle"}
          >
            {t("criteresHorsParc.recherche")}
          </Button>
        </Box>
      </Box>
    </fetcher.Form>
  );
}
